/**
 * Participate-leads triage config (CHAP-339) - shared by client + server.
 *
 * The public omega-zeta.com "Commit now" form writes participate_leads rows
 * (see routes/participate.ts). This module defines the TEAM working-state
 * vocabulary the HQ/tenant triage view uses: a lead's `status` lifecycle
 * (distinct from the applicant's stated `intent` captured at submission),
 * plus display labels for path/intent.
 */

export type ParticipateLeadStatus =
  | 'new'
  | 'contacted'
  | 'working'
  | 'committed'
  | 'declined'
  | 'archived';

export type ParticipatePath =
  | 'give' | 'invest' | 'lease'
  | 'give+invest' | 'give+lease' | 'invest+lease'
  | 'all' | 'unsure';
export type ParticipateIntent = 'commit' | 'pledge' | 'interested' | 'info';

export interface LeadStatusMeta {
  key: ParticipateLeadStatus;
  label: string;
  /** Tailwind badge classes (no shared <Badge> component in this repo). */
  cls: string;
  terminal: boolean;
}

export const LEAD_STATUSES: readonly LeadStatusMeta[] = [
  { key: 'new', label: 'New', cls: 'bg-blue-100 text-blue-800', terminal: false },
  { key: 'contacted', label: 'Contacted', cls: 'bg-indigo-100 text-indigo-800', terminal: false },
  { key: 'working', label: 'Working', cls: 'bg-amber-100 text-amber-900', terminal: false },
  // CHAP-542, Jake 2026-09-26. Labelled "Verbally committed", not "Committed".
  //
  // Since the donor form stopped claiming commitment, this is the ONLY record
  // that anyone committed to anything, and setting it requires no evidence: no
  // signed subscription, no Foundation receipt, not even a mandatory note. The
  // key stays `committed` so no data moves; only what a reader is told changes.
  //
  // The label is the honest description of what the dropdown actually captures,
  // and it keeps a board or Foundation readout from resting the word
  // "committed" on a click.
  { key: 'committed', label: 'Verbally committed', cls: 'bg-green-100 text-green-800', terminal: true },
  { key: 'declined', label: 'Declined', cls: 'bg-gray-200 text-gray-600', terminal: true },
  { key: 'archived', label: 'Archived', cls: 'bg-gray-100 text-gray-500', terminal: true },
];

export const LEAD_STATUS_KEYS = LEAD_STATUSES.map((s) => s.key) as [
  ParticipateLeadStatus,
  ...ParticipateLeadStatus[],
];

export const PATH_META: Record<ParticipatePath, { label: string }> = {
  give: { label: 'Charitable gift' },
  invest: { label: 'Investment' },
  lease: { label: 'Suite lease' },
  'give+invest': { label: 'Gift + Investment' },
  'give+lease': { label: 'Gift + Suite' },
  'invest+lease': { label: 'Investment + Suite' },
  all: { label: 'All three' },
  unsure: { label: 'Undecided' },
};

export const INTENT_META: Record<ParticipateIntent, { label: string }> = {
  commit: { label: 'Ready to commit' },
  pledge: { label: 'Pledge' },
  interested: { label: 'Interested' },
  info: { label: 'Wants info' },
};

/**
 * CHAP-542 - never run `UPDATE participate_leads SET intent = ...`.
 *
 * Until the fix, `participate.js` wrote `intent: 'commit'` for every visitor who
 * ticked a specific path, even though nothing on the donor site ever asked
 * anyone to commit. A row reading `commit` says so because of a bug, not because
 * the person committed to anything.
 *
 * Jake's ruling, 2026-09-26: ANNOTATE, NEVER RELABEL. That is a preservation
 * decision, not a tidiness one. Once a securities matter is reasonably
 * foreseeable, rewriting the rows is the shape of spoliation, so the raw values
 * stay and the caveat travels with them.
 *
 * This lives here, beside the labels, because this module is what every surface
 * that renders a lead already imports. A caveat nobody imports is a caveat
 * nobody sees. The same sentence is also a COMMENT ON COLUMN in the database, so
 * it reaches a CSV export and a psql session that never load this file.
 */
export const INTENT_PRESERVATION_RULE =
  'Rows reading "commit" predate the CHAP-542 fix and mean interest, not commitment. Annotate, never relabel.';

/**
 * The instant the column started telling the truth. Narrative only.
 *
 * Deliberately NOT the thing correctness rests on. It was first set from the
 * authoring clock, which was wrong in the dangerous direction: a timestamp set
 * before the deploy un-caveats bad rows, whereas one set after merely
 * over-caveats good ones. Between authoring and a live donor-site deploy sit CI,
 * review, merge, a static build and up to 300 seconds of Cloudflare edge TTL,
 * and every submission in that window is written by the OLD script.
 *
 * So the predicate below leads on the stored VALUE, which cannot drift.
 */
export const INTENT_TRUSTWORTHY_FROM = '2026-09-26T04:00:00Z';

/**
 * Is this lead's stored `intent` something the current form could have produced?
 *
 * Value first, clock second. After the fix no client can produce `commit`: the
 * page no longer sends it, and the server coerces it away for anything that
 * still does (routes/participate.ts). So a row reading `commit` is a pre-fix row
 * whatever its timestamp says, which is what makes this survive a wrong
 * constant, a stale Cloudflare edge cache, and the long-open-tab case where a
 * browser submits hours-old JavaScript.
 *
 * Fails closed on a missing or unparseable date: an unknown date cannot be shown
 * to be after the cutover, and guessing optimistically is how a caveat quietly
 * stops applying to exactly the rows whose provenance is least clear.
 */
export function intentIsTrustworthy(
  intent: string | null | undefined,
  createdAt: string | null | undefined,
): boolean {
  // A value the current form cannot emit is a pre-fix row, full stop.
  if (intent !== 'interested') return false;
  if (!createdAt) return false;
  const t = Date.parse(createdAt);
  if (Number.isNaN(t)) return false;
  return t >= Date.parse(INTENT_TRUSTWORTHY_FROM);
}

/** Label lookups tolerant of unexpected legacy values (falls back to the raw key). */
export function pathLabel(p: string): string {
  return PATH_META[p as ParticipatePath]?.label ?? p;
}
export function intentLabel(i: string): string {
  return INTENT_META[i as ParticipateIntent]?.label ?? i;
}
export function statusMeta(s: string): LeadStatusMeta {
  return LEAD_STATUSES.find((x) => x.key === s) ?? { key: 'new', label: s, cls: 'bg-gray-100 text-gray-600', terminal: false };
}

export interface ParticipateLeadRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  path: ParticipatePath;
  intent: ParticipateIntent;
  amount: string | null;
  message: string | null;
  source: string | null;
  status: ParticipateLeadStatus;
  ownerUserId: string | null;
  ownerName: string | null;
  notes: string | null;
  workedAt: string | null;
  createdAt: string | null;
}

export interface ParticipateLeadsResponse {
  statuses: LeadStatusMeta[];
  byStatus: Record<string, number>;
  total: number;
  records: ParticipateLeadRow[];
}
